import { useState } from "react";
import axios from "axios";
import { FileText, SpinnerGap } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HojaEncargo = ({ resultado, cuantia }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre_cliente: "",
    nif_cliente: "",
    domicilio_cliente: "",
    telefono_cliente: "",
    email_cliente: "",
    descripcion_servicio: "",
    forma_pago: "A convenir con el cliente",
    incluye_iva: false,
  });

  if (!resultado) return null;

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate-hoja-encargo`, {
        cuantia: resultado.cuantia || cuantia,
        tipo_procedimiento: resultado.tipo_procedimiento,
        honorarios_base: resultado.honorarios_base,
        honorarios_final: resultado.honorarios_final,
        ...formData,
        descripcion_servicio: formData.descripcion_servicio || resultado.tipo_procedimiento,
      }, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hoja_encargo_${Date.now()}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      setShowModal(false);
    } catch (error) {
      console.error('Error generating hoja de encargo:', error);
      alert('Error al generar la hoja de encargo.');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <button
        data-testid="generate-hoja-encargo-button"
        onClick={() => setShowModal(true)}
        className="w-full bg-transparent border-2 border-[#8B1A1A] text-[#8B1A1A] rounded-none px-8 py-4 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#8B1A1A] hover:text-white transition-all flex items-center justify-center gap-2"
      >
        <FileText size={20} weight="bold" />
        Generar hoja de encargo
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div
            className="bg-white border border-[#171717] rounded-none p-6 max-w-lg w-full shadow-[8px_8px_0px_0px_rgba(139,26,26,1)] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="hoja-encargo-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold text-[#0A0A0A]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Hoja de encargo profesional
              </h3>
              <button
                data-testid="hoja-encargo-close"
                onClick={() => setShowModal(false)}
                className="text-[#525252] hover:text-[#0A0A0A] text-xl leading-none font-bold"
              >
                x
              </button>
            </div>

            <p className="text-xs text-[#525252] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Rellena los datos del cliente (opcionales) para generar la plantilla PDF de la hoja de encargo.
            </p>

            {/* Summary */}
            <div className="bg-[#F4F4F0] p-3 mb-4 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-[#525252]">Procedimiento</span>
                <span className="font-mono font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{resultado.tipo_procedimiento}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#525252]">Honorarios</span>
                <span className="font-mono font-bold text-[#8B1A1A]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {resultado.honorarios_final?.toLocaleString('es-ES', { minimumFractionDigits: 2 })}€
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#525252] mb-1">Nombre del cliente</label>
                <input
                  data-testid="hoja-nombre-cliente"
                  type="text"
                  value={formData.nombre_cliente}
                  onChange={(e) => updateField('nombre_cliente', e.target.value)}
                  placeholder="Nombre completo"
                  className="w-full border border-[#171717] rounded-none p-2 text-sm font-mono"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#525252] mb-1">NIF</label>
                  <input
                    data-testid="hoja-nif-cliente"
                    type="text"
                    value={formData.nif_cliente}
                    onChange={(e) => updateField('nif_cliente', e.target.value)}
                    placeholder="12345678A"
                    className="w-full border border-[#171717] rounded-none p-2 text-sm font-mono"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-[#525252] mb-1">Telefono</label>
                  <input
                    data-testid="hoja-telefono-cliente"
                    type="tel"
                    value={formData.telefono_cliente}
                    onChange={(e) => updateField('telefono_cliente', e.target.value)}
                    placeholder="600 000 000"
                    className="w-full border border-[#171717] rounded-none p-2 text-sm font-mono"
                    style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#525252] mb-1">Email del cliente</label>
                <input
                  data-testid="hoja-email-cliente"
                  type="email"
                  value={formData.email_cliente}
                  onChange={(e) => updateField('email_cliente', e.target.value)}
                  placeholder="cliente@email.com"
                  className="w-full border border-[#171717] rounded-none p-2 text-sm font-mono"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#525252] mb-1">Domicilio</label>
                <input
                  data-testid="hoja-domicilio-cliente"
                  type="text"
                  value={formData.domicilio_cliente}
                  onChange={(e) => updateField('domicilio_cliente', e.target.value)}
                  placeholder="Calle, numero, ciudad"
                  className="w-full border border-[#171717] rounded-none p-2 text-sm font-mono"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#525252] mb-1">Descripcion del servicio</label>
                <textarea
                  data-testid="hoja-descripcion-servicio"
                  value={formData.descripcion_servicio}
                  onChange={(e) => updateField('descripcion_servicio', e.target.value)}
                  placeholder={resultado.tipo_procedimiento}
                  rows={2}
                  className="w-full border border-[#171717] rounded-none p-2 text-sm resize-none"
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-[#525252] mb-1">Forma de pago</label>
                <input
                  data-testid="hoja-forma-pago"
                  type="text"
                  value={formData.forma_pago}
                  onChange={(e) => updateField('forma_pago', e.target.value)}
                  className="w-full border border-[#171717] rounded-none p-2 text-sm font-mono"
                  style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer p-2 border border-[#E5E5E2] hover:border-[#171717] transition-colors">
                <input
                  data-testid="hoja-incluye-iva"
                  type="checkbox"
                  checked={formData.incluye_iva}
                  onChange={(e) => updateField('incluye_iva', e.target.checked)}
                  className="w-4 h-4 accent-[#8B1A1A]"
                />
                <span className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Incluir desglose IVA (21%)</span>
              </label>
            </div>

            <button
              data-testid="hoja-encargo-download"
              onClick={handleGenerate}
              disabled={loading}
              className="w-full mt-4 bg-[#8B1A1A] text-white rounded-none px-6 py-3 font-mono uppercase tracking-[0.1em] text-sm hover:bg-[#701515] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <SpinnerGap size={20} className="animate-spin" /> : <FileText size={20} weight="bold" />}
              Descargar hoja de encargo
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default HojaEncargo;
